// Media-oriented objective: tweak weights while reusing core maximize-allocation formula
// Emphasis: stronger penalties on unavailability and understaffing, higher penalty on external usage,
// slightly higher bonus for keeping existing assignments.
import { buildMaximizeAllocationObjective } from './maximizeAllocation';

export function buildMediaMaximizeAllocationObjective(model, params = {}) {
  // Base maximize-allocation with media-tuned weights
  const defaultWeights = {
    assign: 1,
    unavailable: 120,
    understaff: 30,
    keepExisting: 0.2,
    externalDistinct: 0.6,
    externalGate: 0.1,
    // New objective components
    breakCount: 0.5,
    gapPerHour: 1.8,
    usersPerDayPenalty: 0.3,
    // Optional: penalize distinct users per series/role (enabled via preset)
    seriesDistinct: 0,
  };
  const weights = { ...defaultWeights, ...(params?.weights || {}) };
  const base = buildMaximizeAllocationObjective(model, { ...params, weights });

  // Extend objective with penalties for same-day breaks between consecutive events
  const idx = model?.optimizationIndexes || {};
  const edges = Array.isArray(idx.adjacencyEdges) ? idx.adjacencyEdges : [];
  const users = Object.keys(idx.users || {});
  const declaredP = new Set(Object.keys(idx.adjacencyPairDecisionVariables || {}));
  const dayWorkVars = idx?.dayWorkDecisionVariables || {};
  const seriesDistinctVars = idx?.seriesDistinctDecisionVariables || {};

  // Lookup for full gap hours per (a,b)
  const gapHoursByPair = new Map(); // key: `${a}|${b}` -> hours
  for (const edge of edges) {
    const a = edge?.a; const b = edge?.b;
    if (!a || !b) continue;
    const gapMs = Number(edge?.gapMs) || 0;
    const gapH = Math.max(0, gapMs / 3600000);
    gapHoursByPair.set(`${a}|${b}`, gapH);
  }

  const extraVars = [];
  const extraCoeffs = [];
  for (const uKey of users) {
    for (const edge of edges) {
      const a = edge?.a; const b = edge?.b;
      if (!a || !b) continue;
      const name = `p_${uKey}_${a}_${b}`;
      if (!declaredP.has(name)) continue;
      const gapH = gapHoursByPair.get(`${a}|${b}`) || 0;
      const coeff = -(Number(weights.breakCount) || 0) - (Number(weights.gapPerHour) || 0) * gapH;
      if (coeff !== 0) { extraVars.push(name); extraCoeffs.push(coeff); }
    }
  }

  // Penalize distinct users used per day via day-work indicators w_{u,day}
  const usersPerDayCoeff = Number(weights.usersPerDayPenalty) || 0;
  if (usersPerDayCoeff !== 0) {
    for (const name of Object.keys(dayWorkVars)) {
      extraVars.push(name);
      extraCoeffs.push(-usersPerDayCoeff);
    }
  }

  // Penalize distinct users used per series/role via ys_{u,s,r}
  const seriesDistinctCoeff = Number(weights.seriesDistinct) || 0;
  if (seriesDistinctCoeff !== 0) {
    for (const name of Object.keys(seriesDistinctVars)) {
      extraVars.push(name);
      extraCoeffs.push(-seriesDistinctCoeff);
    }
  }

  return {
    maximize: true,
    vars: [...(base.vars || []), ...extraVars],
    coeffs: [...(base.coeffs || []), ...extraCoeffs],
  };
}
